import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';

import { TranslateService } from '@ngx-translate/core';
import { AlertMessageError, AuthMessageError, GroupMessageError } from '@tekman/onmat/models';
import { AlertService } from '@tekman/shared/service';
import { Router } from '@angular/router';
import { appRoutes } from '@tekman/onmat/constants';

@Injectable({
  providedIn: 'root',
})
export class AuthInterceptor implements HttpInterceptor {
  constructor(private readonly alertService: AlertService, private readonly injector: Injector, private router: Router) {}

  private get translate(): TranslateService {
    return this.injector.get(TranslateService);
  }

  intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(req).pipe(catchError(error => this.handleError(error)));
  }

  private handleError(error: HttpErrorResponse) {
    if (error.url.includes('user_intercom_token')) {
      return;
    }
    const messageError = error?.error?.errorCode;
    if (messageError === undefined && error.status === 401) {
      this.router.navigate([appRoutes.error404]);
    } else if (messageError !== undefined && messageError === AuthMessageError.STUDENT_NOT_IN_GROUP) {
      this.router.navigate([appRoutes.errorGroup]);
    } else if (messageError !== undefined && messageError in AuthMessageError) {
      this.router.navigate([appRoutes.error404]);
    }

    // The group import needs the whole 400 body (errorCode + existingGroupId), not just the code.
    if (messageError === GroupMessageError.ALREADY_EXISTING_GROUP_BY_EXTERNAL_GROUP_INFO_GIVEN) {
      return throwError(error);
    }

    if (messageError !== undefined && messageError in AlertMessageError) {
      const message = this.translate.instant(this.mapAlertMessage(messageError));
      this.alertService.openAlert(message, false);
    } else {
      return throwError(messageError);
    }
  }

  private mapAlertMessage(error: string): string {
    let messageError: string;
    switch (error) {
      case AlertMessageError.ACTIVITY_NOT_FOUND:
        messageError = 'f.alert.error.activityNotFound';
        break;
      case AlertMessageError.RUBRIC_CONFIGURATION_NOT_FOUND:
        messageError = 'f.alert.error.rubricConfigurationNotFound';
        break;
      case AlertMessageError.EXERCISE_NOT_FOUND:
        messageError = 'f.alert.error.exerciseNotFound';
        break;
      case AlertMessageError.ACTIVITY_FEATURE_NOT_FOUND:
        messageError = 'f.alert.error.activityFeatureNotFound';
        break;
      case AlertMessageError.MULTIPLE_ANSWER_NOT_FOUND:
        messageError = 'f.alert.error.multipleAnswerNotFound';
        break;
      case AlertMessageError.GROUP_NOT_FOUND:
        messageError = 'f.alert.error.groupNotFound';
        break;
      case AlertMessageError.SCHOOL_NOT_FOUND:
        messageError = 'f.alert.error.schoolNotFound';
        break;
      case AlertMessageError.STUDENT_ANSWERS_CREATE_ERROR:
        messageError = 'f.alert.error.studentAnswersCreateError';
        break;
      case AlertMessageError.STUDENT_FEATURE_NOT_FOUND:
        messageError = 'f.alert.error.studentFeatureNotFound';
        break;
      case AlertMessageError.TEACHER_NOT_FOUND:
        messageError = 'f.alert.error.teacherNotFound';
        break;
      case AlertMessageError.STEP_NOT_FOUND:
        messageError = 'f.alert.error.stepNotFound';
        break;
      default:
        messageError = error;
    }
    return messageError;
  }
}
